var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var TableComponent_1;
import { customElement, property, state } from 'lit/decorators.js';
import { GGOBaseComponent } from '../ggo.base.component.js';
import { html, css, nothing } from 'lit';
import { TableEvent } from './table.event.js';
let TableComponent = TableComponent_1 = class TableComponent extends GGOBaseComponent {
    constructor() {
        super(...arguments);
        this.fields = []; // Column definitions
        this.rows = [];
        this.maxlines = -1;
        this.rowTitle = ''; // Accept string or array
        this.currentPage = 1; // Current page
    }
    static get styles() {
        return [
            ...super.styles,
            css `
        .ggo-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0 0.5rem;
          scrollbar-width: thin;
        }
        .ggo-tbody {
          font-size: 0.875rem;
        }
        th.ggo-th {
          color: #03234d;
          background-color: #ebeef1;
          border-right: solid 1px #ffffff;
        }
        th.ggo-th:hover {
          color: #184989;
        }
        .ggo-th {
          position: -webkit-sticky;
          position: sticky;
          top: 0;
          z-index: 3;
          font-size: 0.813rem;
          border: none;
          padding: 0.5rem 1rem;
          background-color: #f8f9fa;
          text-align: left;
          cursor: default;
        }
        .ggo-th.sortable {
          cursor: pointer;
        }
        .ggo-tr {
          border-radius: 0.5rem;
          overflow: hidden;
          transition: box-shadow 0.2s ease-in-out;
        }
        .ggo-tr:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          cursor: pointer;
        }
        .ggo-tr td:first-child {
          border-top-left-radius: 0.5rem;
          border-bottom-left-radius: 0.5rem;
        }
        .ggo-tr td:last-child {
          border-top-right-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
        }
        .ggo-td {
          background-color: #fff;
          padding: 0.813rem 1rem;
        }
        .ggo-sort {
          font-size: smaller;
        }
        .pagination {
          display: flex;
          justify-content: center;
          margin-top: 10px;
          flex-wrap: wrap;
        }
        .pagination-button {
          margin: 0 5px;
          padding: 5px 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background-color: #fff;
          cursor: pointer;
          transition: background-color 0.2s ease-in-out;
        }
        .pagination-button:hover {
          background-color: #f0f0f0;
        }
        .pagination-button.active {
          background-color: #007bff;
          color: #fff;
          border-color: #007bff;
        }
        table.ggo-table > .ggo-tbody > .ggo-tr > .ggo-td > .slds-truncate {
          max-width: 6rem;
        }
        table.ggo-table > .ggo-tbody > .ggo-tr > .ggo-td:last-child > ggo-dropdown {
          display: flex;
          flex-direction: row-reverse;
        }
      `,
        ];
    }
    update(changedProperties) {
        super.update(changedProperties);
        // Reset current page to 1 if fields or rows change
        if (changedProperties.has('fields') || changedProperties.has('rows')) {
            this.currentPage = 1;
        }
    }
    handleSort(field, index) {
        if (!field.sortDirection)
            field.sortDirection = 'asc';
        // Sort rows on a copy
        this.rows = [...this.rows].sort((a, b) => {
            const aValue = TableComponent_1.getCellValue(a[index]);
            const bValue = TableComponent_1.getCellValue(b[index]);
            if (aValue === undefined && bValue === undefined)
                return 0;
            if (aValue === undefined && bValue !== undefined)
                return 1;
            if (aValue !== undefined && bValue === undefined)
                return -1;
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const aStr = aValue.toLowerCase();
                const bStr = bValue.toLowerCase();
                return field.sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
            }
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return field.sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }
            if (aValue?.getTime && bValue?.getTime) {
                return field.sortDirection === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
            }
            // For other types, use localeCompare if possible
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const comparison = aValue.localeCompare(bValue);
                return field.sortDirection === 'asc' ? comparison : -comparison;
            }
            return 0;
        });
        field.sortDirection = field.sortDirection === 'asc' ? 'desc' : 'asc';
        for (let field of this.fields) {
            if (field !== field) {
                field.sortDirection = undefined; // Reset other fields' sort direction
            }
        }
    }
    handleClick(cell, x, y) {
        const tableEvent = new TableEvent({ cell, x, y });
        this.dispatchEvent(tableEvent);
    }
    handleRowClick(row, y, e) {
        if (e.button === 2) {
            // Right click, do not handle as row click
            return;
        }
        const rowEvent = new CustomEvent('row-click', {
            detail: { row, y, newTab: (e.button === 0 && (e.ctrlKey || e.metaKey || e.middleMouse)) || e.button === 1 },
        });
        this.dispatchEvent(rowEvent);
    }
    get paginatedRows() {
        if (this.maxlines > 1) {
            const start = (this.currentPage - 1) * this.maxlines;
            const end = start + this.maxlines;
            return this.rows.slice(start, end);
        }
        return this.rows;
    }
    handlePageChange(page) {
        this.currentPage = page;
    }
    render() {
        const totalPages = Math.ceil(this.rows.length / this.maxlines);
        return html `
      <div style="${this.getAttribute('style') || ''}; overflow-y: auto;">
        <table class="ggo-table">
          <thead>
            <tr>
              ${this.fields.map((field, index) => field.visible !== false
            ? html `
                      <th
                        class="ggo-th ${field.sortable ? 'sortable' : ''}"
                        ${field.sortable ? html `title="Click to sort"` : nothing}
                        @click=${() => field.sortable && this.handleSort(field, index)}
                      >
                        ${field.name}
                        ${field.sortable && field.sortDirection !== undefined
                ? html `<span class="ggo-sort">${field.sortDirection === 'asc' ? '▲' : '▼'}</span>`
                : ''}
                      </th>
                    `
            : nothing)}
            </tr>
          </thead>
          <tbody class="ggo-tbody">
            ${this.paginatedRows.map((row, y) => html `
                <tr class="ggo-tr" @click="${(e) => this.handleRowClick(row, y, e)}"
              }}">
                  ${this.fields
            .map((field, x) => ({ field, cell: row[x], x }))
            .filter(({ field }) => field.visible !== false)
            .map(({ cell, x }) => {
            return html `
                        <td
                          class="ggo-td"
                          title="${TableComponent_1.getCellValue(cell)}"
                          @click="${() => {
                this.handleClick(TableComponent_1.getCellValue(cell), x, y);
            }}"
                        >
                          ${TableComponent_1.getCellContent(cell)}
                        </td>
                      `;
        })}
                </tr>
              `)}
          </tbody>
        </table>
      </div>
      ${this.maxlines > 1 && totalPages > 1
            ? html `
            <div class="pagination">
              ${Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => html `
                  <button class="pagination-button ${this.currentPage === page ? 'active' : ''}" @click=${() => this.handlePageChange(page)}>
                    ${page}
                  </button>
                `)}
            </div>
          `
            : ''}
    `;
    }
    /**
     * Get the content of a cell in html format
     *
     * @param cell
     * @returns
     */
    static getCellContent(cell) {
        return cell && typeof cell === 'object' && 'cell' in cell ? cell.cell : cell;
    }
    /**
     * Get the value of a cell, which can be a TemplateResult or an object with a 'value' property.
     *
     * @param cell
     * @returns
     */
    static getCellValue(cell) {
        if (cell && typeof cell === 'object' && 'value' in cell) {
            return cell.value;
        }
        // cell is of type TemplateResult or { cell: TemplateResult, value?: any }
        const template = cell && typeof cell === 'object' && 'cell' in cell ? cell.cell : cell;
        //return TableComponent.templateToString(template);
        return nothing;
    }
    /**
     * Convert a TemplateResult to string for display or sorting
     *
     * @param t
     * @returns
     */
    static templateToString(t) {
        try {
            const { strings, values } = t;
            const v = [...values, '']; // add last empty part
            return strings.reduce((acc, s, i) => acc + s + v[i], '');
        }
        catch (e) {
            console.error('Error converting template to string:', e);
            return '';
        }
    }
};
__decorate([
    property({ type: Array })
], TableComponent.prototype, "fields", void 0);
__decorate([
    property({ type: Array })
    // rows can be either an array of html elements or an array of objects { cell: TemplateResult, value: any }
], TableComponent.prototype, "rows", void 0);
__decorate([
    property({ type: Number })
], TableComponent.prototype, "maxlines", void 0);
__decorate([
    property({ type: Array })
], TableComponent.prototype, "rowTitle", void 0);
__decorate([
    state()
], TableComponent.prototype, "currentPage", void 0);
TableComponent = TableComponent_1 = __decorate([
    customElement('ggo-table')
], TableComponent);
export { TableComponent };
